import { useRef, useState, type TouchEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Banner, Button, Caret, Icon, MenuItem, MenuList, Popover, Skeleton } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { CalendarPage } from './CalendarModule';
import { WeekGrid, WeekLegend, splitDayLabel } from './WeekGrid';
import { useCalendarStatistics, useCalendarWeek, type CalendarWeek } from './calendarApi';
import { addDays, berlinInstant, startOfWeek, todayKey } from './dates';
import styles from './WeekPage.module.css';

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

function timeOf(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** The user pill; admins and sales leads switch to a member of their span (the statistics' team list). */
function UserPill({
  week,
  onPick,
}: {
  week: CalendarWeek | undefined;
  onPick: (userId: string | null) => void;
}) {
  const permissions = usePermissions();
  const mayPick =
    permissions.is('settings.organization', 'yes') || permissions.is('settings.organization', 'team');
  const team = useCalendarStatistics('30d', mayPick);
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const label = week?.user.shortName ?? '';
  const members = team.data?.team ?? [];
  if (!mayPick || members.length < 2) return <span className={styles.userPill}>{label}</span>;
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={styles.userPillButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Kalender von ${label} · Berater wechseln`}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <Caret size={9} />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchor} initialFocus="none" width={240}>
        <MenuList label="Berater" onClose={() => setOpen(false)}>
          {members.map((member) => (
            <MenuItem
              key={member.userId}
              label={member.name}
              description={member.email}
              active={member.userId === week?.user.id}
              onSelect={() => {
                setOpen(false);
                onPick(member.userId);
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

function WeekSkeleton() {
  return (
    <div className={styles.skeleton} aria-busy="true" aria-label="Kalender wird geladen">
      <Skeleton height={44} />
      <Skeleton height={504} />
    </div>
  );
}

/**
 * Beratung 01 — `/calendar/week?start=&user=`: the week of the signed-in user (or, for admins and sales
 * leads, of a member of their span); 390 px shows one day at a time with arrows and swipe.
 */
export function WeekPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const startParam = params.get('start');
  const userId = params.get('user');
  const today = todayKey();
  const selected = startParam && DAY_KEY.test(startParam) ? startParam : today;
  const monday = startOfWeek(selected);
  const week = useCalendarWeek(monday, userId);
  const touchX = useRef<number | null>(null);

  function go(day: string | null) {
    const next = new URLSearchParams(params);
    if (day === null) next.delete('start');
    else next.set('start', day);
    setParams(next);
  }

  function pickUser(id: string | null) {
    const next = new URLSearchParams(params);
    if (id === null) next.delete('user');
    else next.set('user', id);
    setParams(next);
  }

  function pickTime(day: string, minutes: number) {
    const startsAt = berlinInstant(day, timeOf(minutes));
    if (new Date(startsAt).getTime() <= Date.now()) return;
    void navigate(`/calendar/new?startsAt=${encodeURIComponent(startsAt)}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  }

  const data = week.data;
  const status = week.error instanceof ApiError ? week.error.status : null;

  const navigation = data ? (
    <span className={styles.nav}>
      <button
        type="button"
        className={styles.navButton}
        aria-label="Vorherige Woche"
        onClick={() => go(addDays(monday, -7))}
      >
        ‹
      </button>
      <span className={styles.weekLabel} aria-live="polite">
        {data.weekLabel}
      </span>
      <button
        type="button"
        className={styles.navButton}
        aria-label="Nächste Woche"
        onClick={() => go(addDays(monday, 7))}
      >
        ›
      </button>
      <button type="button" className={styles.todayButton} onClick={() => go(null)}>
        Heute
      </button>
    </span>
  ) : null;

  const day = data?.days.find((entry) => entry.date === selected) ?? data?.days[0];

  function onTouchStart(event: TouchEvent<HTMLDivElement>) {
    touchX.current = event.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const startX = touchX.current;
    const endX = event.changedTouches[0]?.clientX;
    touchX.current = null;
    if (startX === null || endX === undefined || Math.abs(endX - startX) < 60) return;
    go(addDays(day?.date ?? selected, endX < startX ? 1 : -1));
  }

  return (
    <CalendarPage
      tab="week"
      pill={<UserPill week={data} onPick={pickUser} />}
      summary={data ? <span className={styles.summary}>{data.statusLine}</span> : null}
      tabEnd={mobile ? null : navigation}
    >
      {week.isError && !data ? (
        <Banner
          tone="danger"
          className={styles.banner}
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={week.isFetching}
              onClick={() => void week.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Der Kalender konnte nicht geladen werden{status !== null ? ` · Fehler ${status}` : ''}.{' '}
          {errorMessage(week.error)}
        </Banner>
      ) : !data ? (
        <WeekSkeleton />
      ) : mobile && day ? (
        <div className={styles.mobile} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className={styles.dayNav}>
            <button
              type="button"
              className={styles.dayNavButton}
              aria-label="Vorheriger Tag"
              onClick={() => go(addDays(day.date, -1))}
            >
              <Icon icon={ChevronLeft} size={20} />
            </button>
            <span className={styles.dayNavLabel} aria-live="polite">
              {splitDayLabel(day.label).eyebrow} {splitDayLabel(day.label).number}
              <span className={styles.dayNavWeek}>{data.weekLabel}</span>
            </span>
            <button
              type="button"
              className={styles.dayNavButton}
              aria-label="Nächster Tag"
              onClick={() => go(addDays(day.date, 1))}
            >
              <Icon icon={ChevronRight} size={20} />
            </button>
          </div>
          {day.date !== today ? (
            <button type="button" className={styles.todayButton} onClick={() => go(null)}>
              Heute
            </button>
          ) : null}
          <WeekGrid week={data} days={[day]} onPickTime={pickTime} />
          <WeekLegend />
        </div>
      ) : (
        <>
          <WeekGrid week={data} onPickTime={pickTime} />
          <WeekLegend />
        </>
      )}
    </CalendarPage>
  );
}
